/**
 * Access guards for AMS and FMA apps
 * Implements paywall logic based on entitlements
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/global/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/global/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/global/components/ui/card";
import { Lock, LogIn, DollarSign, Mail, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AccessGuardProps {
  app: "AMS" | "FMA";
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface AccessCheck {
  hasAccess: boolean;
  fmaPlan: string | null;
  hasAmsAccess: boolean;
  loading: boolean;
  error?: string;
}

export function AccessGuard({ app, children, fallback }: AccessGuardProps) {
  const { user, activeTenantId, loading: authLoading } = useAuth();
  const [accessCheck, setAccessCheck] = useState<AccessCheck>({
    hasAccess: false,
    fmaPlan: null,
    hasAmsAccess: false,
    loading: true,
  });

  useEffect(() => {
    async function checkAccess() {
      if (!user || !activeTenantId || authLoading) {
        setAccessCheck((prev) => ({ ...prev, loading: authLoading }));
        return;
      }

      try {
        // Use the new effective access view for consolidated access check
        // Note: May return multiple rows if org has multiple subscriptions (AMS + FMA plans)
        // We take the first row since org-level access is consistent across all rows
        const { data: accessDataArray, error: accessError } = await supabase
          .from("v_org_effective_access")
          .select("*")
          .eq("organization_id", activeTenantId)
          .limit(1);

        if (accessError) {
          throw new Error(`Failed to check access: ${accessError.message}`);
        }

        if (!accessDataArray || accessDataArray.length === 0) {
          throw new Error("Organization not found or access denied");
        }

        const accessData = accessDataArray[0];

        const hasAmsAccess =
          accessData.has_ams_access && accessData.has_active_billing;

        let hasAccess = false;
        if (app === "FMA") {
          // FMA access - all organizations have basic FMA access
          hasAccess = accessData.has_fma_access;
        } else if (app === "AMS") {
          // AMS requires explicit access entitlement AND active billing
          hasAccess = hasAmsAccess;
        }

        setAccessCheck({
          hasAccess,
          fmaPlan: accessData.fma_plan,
          hasAmsAccess,
          loading: false,
        });
      } catch (error) {
        console.error("Access check failed:", error);
        setAccessCheck({
          hasAccess: false,
          fmaPlan: null,
          hasAmsAccess: false,
          loading: false,
          error: error instanceof Error ? error.message : "Access check failed",
        });
      }
    }

    checkAccess();
  }, [user, activeTenantId, authLoading, app]);

  if (authLoading || accessCheck.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Ready. Set. Go!</p>
        </div>
      </div>
    );
  }

  if (accessCheck.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">
              Access Check Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{accessCheck.error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!accessCheck.hasAccess) {
    return (
      fallback || <PaywallScreen app={app} fmaPlan={accessCheck.fmaPlan} />
    );
  }

  return <>{children}</>;
}

interface PaywallScreenProps {
  app: "AMS" | "FMA";
  fmaPlan: string | null;
}

function PaywallScreen({ app, fmaPlan }: PaywallScreenProps) {
  const navigate = useNavigate();

  const handleViewPricing = () => {
    // Navigate to pricing page - adjust path as needed
    window.location.href = "/pricing"; // or use navigate("/pricing") if it's in the same app
  };

  const handleLogin = () => {
    navigate("/login");
  };

  if (app === "AMS") {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: "linear-gradient(135deg, #0B1120 0%, #1a1f35 50%, #0B1120 100%)",
        }}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <Card 
          className="w-full max-w-2xl relative overflow-hidden border-orange-500/20"
          style={{
            background: "rgba(11, 17, 32, 0.8)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 107, 53, 0.1), inset 0 2px 0 rgba(255, 255, 255, 0.05)",
          }}
        >
          {/* Gradient border effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-orange-500/20 pointer-events-none"></div>
          
          <CardHeader className="text-center relative pb-6">
            <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 mx-auto">
              <Lock className="w-10 h-10 text-orange-400" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              AMS Access Required
            </CardTitle>
            <p className="text-white/60 mt-2 text-base">
              Unlock the full power of agency management
            </p>
          </CardHeader>

          <CardContent className="space-y-8 relative">
            {/* Current plan display */}
            {fmaPlan && (
              <div 
                className="p-5 rounded-xl border border-orange-500/20 relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 107, 53, 0.05) 0%, rgba(255, 107, 53, 0.02) 100%)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <p className="text-sm text-orange-400 font-medium mb-1">Current FMA Plan</p>
                  <p className="text-2xl font-bold text-white capitalize">{fmaPlan.replace('_', ' ')}</p>
                  {fmaPlan !== "fma_elite" && (
                    <p className="text-sm text-white/60 mt-2">
                      Upgrade to FMA Elite to get AMS access included
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Features list */}
            <div className="space-y-3">
              <p className="text-white/80 text-center mb-4">
                Access to the AMS (Agency Management System) requires an active subscription or FMA Elite plan.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Client Management",
                  "Agent Tracking",
                  "Sales Analytics",
                  "Task Automation",
                  "Team Collaboration",
                  "Advanced Reporting",
                ].map((feature, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-white/70 text-sm"
                  >
                    <Zap className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-3 pt-2">
              <Button
                onClick={handleViewPricing}
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-0 shadow-lg shadow-orange-500/25"
              >
                <DollarSign className="w-5 h-5 mr-2" />
                {fmaPlan === "fma_pro" ? "Upgrade to Elite" : "View Pricing & Subscribe"}
              </Button>
              
              <Button
                onClick={handleLogin}
                variant="outline"
                className="w-full h-12 text-base font-medium border-orange-500/30 hover:bg-orange-500/10 hover:border-orange-500/50 text-white"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Log In
              </Button>
            </div>

            {/* Support link */}
            <div className="text-center pt-4 border-t border-white/5">
              <p className="text-sm text-white/50">
                Need help?{" "}
                <a
                  href="mailto:Support@findmyagency.net"
                  className="text-orange-400 hover:text-orange-300 transition-colors inline-flex items-center gap-1 font-medium"
                >
                  <Mail className="w-4 h-4" />
                  Contact Support
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // This shouldn't happen for FMA, but just in case
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "linear-gradient(135deg, #0B1120 0%, #1a1f35 50%, #0B1120 100%)",
      }}
    >
      <Card 
        className="w-full max-w-lg relative overflow-hidden border-orange-500/20"
        style={{
          background: "rgba(11, 17, 32, 0.8)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 107, 53, 0.1)",
        }}
      >
        <CardHeader className="text-center">
          <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 mx-auto">
            <Lock className="w-8 h-8 text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
            Access Required
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-white/70">
            You don't have access to this application.
          </p>
          <Button 
            onClick={handleLogin}
            className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <LogIn className="w-5 h-5 mr-2" />
            Log In
          </Button>
          <div className="text-center text-sm text-white/50">
            <a
              href="mailto:Support@findmyagency.net"
              className="text-orange-400 hover:text-orange-300 transition-colors inline-flex items-center gap-1"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
